import type { MessagesShape } from './types.js';

export const enUS: MessagesShape = {
  common: {
    ok: 'OK',
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    close: 'Close',
    back: 'Back',
    search: 'Search',
    loading: 'Loading',
    empty: 'Nothing here',
    retry: 'Retry',
    clear: 'Clear',
    remove: 'Remove',
  },
  dialog: {
    confirm: {
      title: 'Confirm delete',
      body: 'This action cannot be undone.',
      confirmBtn: 'Delete',
    },
  },
  toolbar: {
    theme: 'Theme',
    density: 'Density',
    accent: 'Accent',
    layout: 'Layout',
    language: 'Language',
    radius: 'Radius',
    surface: 'Surface',
    motion: 'Motion',
    shadow: 'Shadow',
    typescale: 'Type scale',
    grid: 'Grid',
  },
  layout: {
    writing: 'Writing',
    reading: 'Reading',
    focus: 'Focus',
    research: 'Research',
    review: 'Review',
    immersive: 'Immersive',
  },
  a11y: {
    closeButton: 'Close',
    menuButton: 'Menu',
    openMenu: 'Open navigation',
    closeMenu: 'Close navigation',
    skipToMain: 'Skip to main content',
    skipToNav: 'Skip to navigation',
    liveRegionLabel: 'Status announcements',
  },
  select: {
    searchPlaceholder: 'Search…',
    empty: 'No options',
    noMatch: 'No match for "{query}"',
    announceSelected: 'Selected {label}',
    defaultLabel: 'Select',
  },
  selectTag: {
    add: 'Add',
    placeholder: 'Select tags',
    createOption: 'Add "{value}"',
    removeLabel: 'Remove {label}',
    selectedCount: '{count} selected',
  },
  input: {
    clearLabel: 'Clear',
    defaultLabel: 'Text input',
  },
  textarea: {
    clearLabel: 'Clear',
    defaultLabel: 'Multiline input',
  },
  chip: {
    removeLabel: 'Remove',
    defaultLabel: 'Chip',
  },
  badge: {
    defaultLabel: 'Badge',
  },
  tag: {
    defaultLabel: 'Tag',
    removeLabel: 'Remove',
  },
  kbd: {
    defaultLabel: 'Shortcut key',
  },
  slider: {
    defaultLabel: 'Slider',
  },
  progress: {
    defaultLabel: 'Progress',
  },
  spinner: {
    defaultLabel: 'Loading',
  },
  switch: {
    defaultLabel: 'Switch',
  },
  checkbox: {
    defaultLabel: 'Checkbox',
  },
  radio: {
    defaultLabel: 'Radio',
  },
  segmented: {
    defaultLabel: 'Segmented control',
  },
  menu: {
    defaultLabel: 'Menu',
  },
  commandPalette: {
    defaultLabel: 'Command palette',
    searchPlaceholder: 'Type a command or search…',
    empty: 'No matching commands',
    navigateHint: 'Navigate',
    selectHint: 'Select',
    closeHint: 'Close',
  },
  drawer: {
    defaultLabel: 'Drawer panel',
  },
  iconButton: {
    loadingLabel: 'Loading',
    defaultLabel: 'Icon button',
  },
  playground: {
    brand: 'ViquardDoc',
    tagline: 'doc-ui playground',
    menuOpen: 'Open menu',
    menuClose: 'Close menu',
    storiesNav: 'Stories navigation',
    breadcrumbLabel: 'Location',
    inspector: {
      eyebrow: 'Inspector',
      emptyTitle: 'Prop matrix',
      emptyBody:
        "This story doesn't publish inspector content yet. The side column is reserved for size, variant, and shape matrices so the center stays focused on live interactions.",
      sectionMeta: 'Context',
      metaStory: 'story',
      metaGroup: 'group',
      metaTheme: 'theme',
      metaAccent: 'accent',
      metaLocale: 'locale',
    },
  },
  stories: {
    groups: {
      foundation: 'Foundation',
      inputs: 'Inputs',
      display: 'Display',
      feedback: 'Feedback',
      boundary: 'Boundary',
      icons: 'Icons',
      overlays: 'Overlays',
      menus: 'Menus',
      navigation: 'Navigation',
      a11y: 'Accessibility',
      shortcuts: 'Keyboard',
      data: 'Data',
      layout: 'Layout',
    },
    titles: {
      theme: 'Theme',
      iconGallery: 'Icon gallery',
      button: 'Button',
      iconButton: 'Icon button',
      forms: 'Forms',
      select: 'Select',
      segmented: 'Segmented',
      slider: 'Slider',
      display: 'Display',
      feedback: 'Feedback',
      boundary: 'Boundary',
      overlays: 'Overlays & dialogs',
      menus: 'Menu family',
      commandPalette: 'Command palette',
      drawer: 'Drawer',
      tabs: 'Tabs',
      accordion: 'Accordion',
      breadcrumb: 'Breadcrumb',
      avatar: 'Avatar',
      tree: 'Tree',
      table: 'Table',
      list: 'List',
      timeline: 'Timeline',
      splitPane: 'Split pane',
      numberInput: 'Number input',
      pagination: 'Pagination',
      a11yUtilities: 'A11y utilities',
      shortcuts: 'Shortcuts',
      dataView: 'Data views',
      virtualization: 'Virtualization',
      newInputs: 'Complex inputs',
      presence: 'Presence',
      settingsHub: 'Settings Hub',
      formsKit: 'Forms kit',
      dndDemo: 'Drag & drop',
    },
  },
  axis: {
    density: { compact: 'Compact', comfortable: 'Comfortable', spacious: 'Spacious' },
    radius: { crisp: 'Crisp', soft: 'Soft', pillowy: 'Pillowy', velvet: 'Velvet' },
    surface: { floating: 'Floating', flush: 'Flush', hybrid: 'Hybrid' },
    motion: {
      reduced: 'Reduced',
      performance: 'Performance',
      standard: 'Standard',
      advanced: 'Advanced',
      rich: 'Rich',
    },
    shadow: { flat: 'Flat', soft: 'Soft', pronounced: 'Pronounced', dramatic: 'Dramatic' },
    typescale: { compact: 'Compact', standard: 'Standard', large: 'Large' },
    grid: { dots: 'Dots', grid: 'Grid', lines: 'Lines', blank: 'Blank' },
    theme: {
      system: 'System',
      light: 'Light',
      dark: 'Dark',
      sepia: 'Sepia',
      eyecare: 'Eye-care',
      chalkboard: 'Chalkboard',
      parchment: 'Parchment',
    },
    accent: {
      ochre: 'Ochre',
      ink: 'Ink',
      moss: 'Moss',
      indigo: 'Indigo',
      crimson: 'Crimson',
      custom: 'Custom',
    },
    locale: { 'zh-CN': '中文', 'en-US': 'English' },
  },
};
